import React, { useState, useEffect } from 'react';
import {
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  Paper,
  Divider,
  InputAdornment
} from '@mui/material';
import {
  Person,
  Phone,
  Security,
  Store as StoreIcon
} from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import api from '../../utils/api';
import { cacheEndpoints } from '../../utils/apiCache';

const AddUser = () => {
  const { actions } = useApp();
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [formData, setFormData] = useState({
    f_name: '',
    role: 'user',
    mobileNo: '',
    linkedStore: ''
  });

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      const storesData = await cacheEndpoints.stores.getAll();
      setStores(storesData || []);
    } catch (error) {
      console.error('Error fetching stores:', error);
      actions.showSnackbar('Error fetching stores', 'error');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const errors = [];
    
    if (!formData.f_name.trim()) errors.push('Name is required');
    if (!formData.mobileNo.trim()) errors.push('Mobile number is required');
    if (!/^[6-9]\d{9}$/.test(formData.mobileNo)) {
      errors.push('Valid Indian mobile number is required');
    }
    if (formData.role === 'store_admin' && !formData.linkedStore) {
      errors.push('Store selection is required for Store Admin role');
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (errors.length > 0) {
      actions.showSnackbar(errors[0], 'error');
      return;
    }

    setLoading(true);
    try {
      await api.post('/users', formData);
      actions.showSnackbar('User added successfully!', 'success');
      
      // Reset form
      setFormData({
        f_name: '',
        role: 'user',
        mobileNo: '',
        linkedStore: ''
      });
      
    } catch (error) {
      actions.showSnackbar(error.response?.data?.message || 'Error adding user', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Person sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Add New User
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <form onSubmit={handleSubmit}>
          <Box sx={{ maxWidth: 600, mx: 'auto' }}>
            {/* User Information */}
            <Typography variant="h6" sx={{ mb: 3, color: 'primary.main' }}>
              User Information
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
              <TextField
                fullWidth
                label="Full Name"
                name="f_name"
                value={formData.f_name}
                onChange={handleInputChange}
                required
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person />
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                fullWidth
                label="Mobile Number"
                name="mobileNo"
                value={formData.mobileNo}
                onChange={handleInputChange}
                required
                variant="outlined"
                inputProps={{ maxLength: 10 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone />
                    </InputAdornment>
                  )
                }}
                helperText="Enter 10-digit Indian mobile number"
              />

              <FormControl fullWidth required>
                <InputLabel>Role</InputLabel>
                <Select
                  name="role"
                  value={formData.role}
                  label="Role"
                  onChange={handleInputChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Security />
                      </InputAdornment>
                    )
                  }}
                >
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="store_admin">Store Admin</MenuItem>
                  <MenuItem value="super_admin">Super Admin</MenuItem>
                </Select>
              </FormControl>

              {formData.role === 'store_admin' && (
                <FormControl fullWidth required>
                  <InputLabel>Linked Store</InputLabel>
                  <Select
                    name="linkedStore"
                    value={formData.linkedStore}
                    label="Linked Store"
                    onChange={handleInputChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <StoreIcon />
                        </InputAdornment>
                      )
                    }}
                  >
                    {stores.map((store) => (
                      <MenuItem key={store.id} value={store.id}>
                        {store.storeName} ({store.branchId})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>

            {/* Submit Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ minWidth: 200 }}
              >
                {loading ? 'Adding User...' : 'Add User'}
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default AddUser; 